import { z } from 'zod';
/**
 * Fahrgast-App (KON-263): Verträge zwischen der App der Fahrgäste und der API. Die App gehört zu einem Betrieb (Mandanten), der Fahrgast registriert sich dort und bucht Fahrten.
 */
/** Regeln für Stornierung und Änderung. Platzhalter: Der Betrieb legt später fest, bis wann und unter welchen Bedingungen. Der Server rechnet nur mit diesen Werten, die App liest das Ergebnis (canCancel, canChange). */
export const PASSENGER_RULES = {
    /** Eine Fahrt lässt sich bis so viele Stunden vor der Abholung stornieren */
    cancelUntilHoursBefore: 2,
    /** Angaben einer Fahrt lassen sich bis so viele Stunden vor der Abholung ändern */
    changeUntilHoursBefore: 24,
};
/** Punkte je volle ausgegebene Euro und die Stufen des Treueprogramms. Entwurf, noch nicht mit dem Betrieb abgestimmt. */
export const LOYALTY_POINTS_PER_EURO = 1;
export const LOYALTY_TIERS = [
    { key: 'bronze', label: 'Bronze', from: 0, perk: 'Sammeln Sie Punkte mit jeder Fahrt.' },
    { key: 'silver', label: 'Silber', from: 250, perk: 'Bevorzugte Buchung zu Stoßzeiten.' },
    { key: 'gold', label: 'Gold', from: 1000, perk: 'Kostenlose Wartezeit bis 15 Minuten.' },
    { key: 'platinum', label: 'Platin', from: 3000, perk: 'Persönlicher Ansprechpartner im Büro.' },
];
const text = (label, min, max) => z.string().trim().min(min, `${label} fehlt`).max(max, `${label} ist zu lang`);
const optional = (max) => z.string().trim().max(max).optional().transform((v) => (v ? v : undefined));
const email = z.string().trim().toLowerCase().pipe(z.email('Bitte prüfen Sie die E-Mail-Adresse'));
const password = z.string().min(10, 'Mindestens 10 Zeichen').max(200, 'Höchstens 200 Zeichen');
export const passengerRegisterInput = z
    .object({
    name: text('Name', 2, 120),
    email,
    password,
    phone: optional(40),
    acceptTerms: z.literal(true, { error: 'Bitte bestätigen Sie die Datenschutzerklärung und die Bedingungen' }),
    deviceName: optional(120),
})
    .strict();
export const passengerLoginInput = z.object({ email, password: z.string().min(1, 'Passwort fehlt').max(200), deviceName: optional(120) }).strict();
export const passengerProfileInput = z
    .object({
    name: text('Name', 2, 120).optional(),
    phone: z.string().trim().max(40).nullable().optional(),
    street: z.string().trim().max(120).nullable().optional(),
    postalCode: z.string().trim().max(10).nullable().optional(),
    city: z.string().trim().max(80).nullable().optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const passengerPasswordInput = z.object({ currentPassword: z.string().min(1, 'Aktuelles Passwort fehlt').max(200), newPassword: password }).strict();
export const PASSENGER_VEHICLE_CLASSES = ['business', 'first', 'van', 'hybrid', 'electric'];
export const PASSENGER_VEHICLE_CLASS_LABELS = {
    business: 'Business Limousine',
    first: 'First Class',
    van: 'Business Van',
    hybrid: 'Hybrid Limousine',
    electric: 'Elektro Limousine',
};
const rideDate = z.iso.date('Bitte ein Datum angeben');
const rideTime = z.string().trim().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Bitte die Uhrzeit so angeben: 14:30');
export const passengerRideInput = z
    .object({
    from: text('Abholadresse', 3, 200),
    to: text('Zieladresse', 3, 200),
    date: rideDate,
    time: rideTime,
    passengers: z.number().int().min(1, 'Mindestens 1 Person').max(8, 'Höchstens 8 Personen').default(1),
    luggage: z.number().int().min(0).max(20).optional(),
    flight: optional(20),
    vehicleClass: z.enum(PASSENGER_VEHICLE_CLASSES).optional(),
    note: optional(1000),
    paymentMethodId: z.uuid().optional(),
    voucherCode: optional(40),
})
    .strict();
/** Was sich an einer Fahrt ändern lässt (Bedingungen siehe PASSENGER_RULES, später genauer) */
export const passengerRideChangeInput = z
    .object({
    from: text('Abholadresse', 3, 200).optional(),
    to: text('Zieladresse', 3, 200).optional(),
    date: rideDate.optional(),
    time: rideTime.optional(),
    passengers: z.number().int().min(1).max(8).optional(),
    /** null hebt die Wahl auf, dann wählt das Büro eine passende Klasse */
    vehicleClass: z.enum(PASSENGER_VEHICLE_CLASSES).nullable().optional(),
    flight: z.string().trim().max(20).nullable().optional(),
    note: z.string().trim().max(1000).nullable().optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const passengerCancelInput = z.object({ reason: optional(500) }).strict();
const stars = (label) => z.number(`Bitte Sterne für ${label} wählen`).int().min(1, 'Mindestens 1 Stern').max(5, 'Höchstens 5 Sterne');
export const passengerRatingInput = z.object({ driverStars: stars('den Fahrer'), vehicleStars: stars('das Fahrzeug'), serviceStars: stars('den Service'), comment: optional(1000) }).strict();
export const passengerChatInput = z.object({ body: text('Nachricht', 1, 1000) }).strict();
export const PASSENGER_PAYMENT_KINDS = ['invoice', 'cash', 'card_vehicle', 'card_online'];
export const PASSENGER_PAYMENT_LABELS = {
    invoice: 'Auf Rechnung',
    cash: 'Bar im Fahrzeug',
    card_vehicle: 'Karte im Fahrzeug',
    card_online: 'Gespeicherte Karte',
};
export const passengerPaymentInput = z
    .object({
    kind: z.enum(PASSENGER_PAYMENT_KINDS, 'Bitte eine Zahlungsart wählen'),
    /** Nur bei gespeicherten Karten: Kennung beim Zahlungsdienst, nie die Kartennummer */
    providerRef: optional(120),
    brand: optional(30),
    last4: z.string().trim().regex(/^\d{4}$/, 'Vier Ziffern').optional(),
    expires: z.string().trim().regex(/^(0[1-9]|1[0-2])\/\d{2}$/, 'So angeben: 08/28').optional(),
    makeDefault: z.boolean().default(false),
})
    .strict();
export const passengerVoucherCheckInput = z.object({ code: text('Rabattcode', 3, 40) }).strict();
/** Rabattcode im Backoffice */
export const voucherInput = z
    .object({
    code: z.string().trim().toUpperCase().regex(/^[A-Z0-9-]{3,30}$/, 'Nur Buchstaben, Ziffern und Bindestrich, 3 bis 30 Zeichen'),
    kind: z.enum(['percent', 'amount'], 'Bitte die Art wählen'),
    /** Prozent (1 bis 100) oder Betrag in Cent */
    value: z.number().int().min(1, 'Wert fehlt'),
    validFrom: z.iso.date().nullable().optional(),
    validUntil: z.iso.date().nullable().optional(),
    maxUses: z.number().int().min(1).nullable().optional(),
    perPerson: z.number().int().min(1).max(100).default(1),
    note: z.string().trim().max(300).nullable().optional(),
})
    .strict()
    .refine((v) => v.kind !== 'percent' || v.value <= 100, { message: 'Höchstens 100 Prozent', path: ['value'] })
    .refine((v) => !v.validFrom || !v.validUntil || v.validFrom <= v.validUntil, { message: 'Das Ende liegt vor dem Beginn', path: ['validUntil'] });
export const voucherPatchInput = z.object({ active: z.boolean() }).strict();
/** Stand einer Fahrt aus Sicht des Fahrgasts */
export const PASSENGER_RIDE_STATUSES = ['requested', 'offer', 'confirmed', 'driver_assigned', 'running', 'done', 'cancelled', 'declined'];
export const PASSENGER_RIDE_STATUS_LABELS = {
    requested: 'Angefragt',
    offer: 'Angebot liegt vor',
    confirmed: 'Bestätigt',
    driver_assigned: 'Fahrer zugewiesen',
    running: 'Unterwegs',
    done: 'Abgeschlossen',
    cancelled: 'Storniert',
    declined: 'Abgelehnt',
};
export const passengerOfferRejectInput = z.object({ reason: optional(500) }).strict();
export const passengerDeviceInput = z
    .object({
    pushToken: z.string().trim().min(10).max(400),
    platform: z.enum(['android', 'ios']).default('android'),
    deviceName: optional(100),
    appVersion: optional(30),
})
    .strict();
//# sourceMappingURL=passenger.js.map